import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";

// LanguageSelector — a pill dropdown that persists the user's choice in
// localStorage and offers a `translate(text, cacheKey)` helper via the
// returned hook `useTranslate(cacheKey)` (see below).

export const LANG_STORAGE_KEY = "np_lang";
export const LANG_CHANGE_EVENT = "np:lang-change";

export function getLang() {
  try { return window.localStorage.getItem(LANG_STORAGE_KEY) || "en"; } catch { return "en"; }
}
export function setLang(code) {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, code);
    window.dispatchEvent(new CustomEvent(LANG_CHANGE_EVENT, { detail: code }));
  } catch {}
}

export function useLang() {
  const [lang, setLangState] = useState(getLang());
  useEffect(() => {
    const on = (e) => setLangState(e.detail || getLang());
    window.addEventListener(LANG_CHANGE_EVENT, on);
    return () => window.removeEventListener(LANG_CHANGE_EVENT, on);
  }, []);
  return lang;
}

// Fetches supported list once
export function useLanguages() {
  const [langs, setLangs] = useState([{ code: "en", label: "English" }]);
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await http.get("/translate/languages");
        if (ok) setLangs(data.languages || []);
      } catch { /* keep default */ }
    })();
    return () => { ok = false; };
  }, []);
  return langs;
}

// useTranslate(cacheKey) -> (text) => translated string.
// Caches translated results in memory keyed by (lang, text). English pass-through.
const memoryCache = new Map(); // `${lang}::${slug}::${sha}` -> string

async function callTranslate(text, target_lang, cacheKey) {
  const { data } = await http.post("/translate", { text, target_lang, cache_key: cacheKey });
  return data.translated_text;
}

export function useTranslated(text, cacheKey) {
  const lang = useLang();
  const [out, setOut] = useState(text);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!text) { setOut(text); return; }
    if (lang === "en") { setOut(text); return; }
    const k = `${lang}::${cacheKey || "adhoc"}::${text.slice(0, 40)}::${text.length}`;
    if (memoryCache.has(k)) { setOut(memoryCache.get(k)); return; }
    setLoading(true);
    try {
      const t = await callTranslate(text, lang, cacheKey);
      memoryCache.set(k, t);
      setOut(t);
    } catch { setOut(text); }
    finally { setLoading(false); }
  }, [text, lang, cacheKey]);

  useEffect(() => { load(); }, [load]);
  return { text: out, loading, lang };
}

export default function LanguageSelector({ variant = "dark" }) {
  const langs = useLanguages();
  const current = useLang();
  const [open, setOpen] = useState(false);

  const label = (langs.find(l => l.code === current) || { label: "English" }).label;
  const isDark = variant === "dark";

  return (
    <div className="relative inline-block">
      <button
        data-testid="lang-selector-btn"
        onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase border transition-colors ${
          isDark
            ? "border-[#F5EBDC]/30 text-[#F5EBDC] hover:bg-[#F5EBDC] hover:text-[#0C2D1F]"
            : "border-black/20 text-[color:var(--np-text)] hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)]"
        }`}
      >
        🌐 <span>{label}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className={`absolute right-0 mt-2 z-[70] w-64 max-h-96 overflow-y-auto shadow-2xl ${
            isDark ? "bg-[#0C2D1F] border border-[#F5EBDC]/15" : "bg-[color:var(--np-bg)] border border-black/15"
          }`}>
            {langs.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full text-left px-4 py-2 font-mono-tech text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  isDark
                    ? `${current === l.code ? "text-[#E4A15C] bg-[#F5EBDC]/5" : "text-[#E5D8C4]/80"} hover:bg-[#F5EBDC]/8 hover:text-[#F5EBDC]`
                    : `${current === l.code ? "text-[color:var(--np-brand)] bg-black/5" : "text-[color:var(--np-text-2)]"} hover:bg-black/5 hover:text-[color:var(--np-text)]`
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
